/**
 * 卡牌项组件 - 扑克牌竖版高窄布局
 * 
 * 颜色由卡牌类型决定（话题=蓝、事件=橙、人设=紫）
 * 左上角小徽章显示风险等级（safe=绿、normal=橙、risky=红）
 */

import React from 'react';
import { Star, Zap, Check, Trash2 } from 'lucide-react';
import type { GameCard } from '../../../types';
import { CARD_TYPE_STYLE_CONFIG, RISK_STYLES, CARD_TYPE_ICON } from '../../../constants/styles';

interface CardItemProps {
  card: GameCard;
  /** 是否选中 */
  isSelected?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 选择模式 */
  selectionMode?: 'none' | 'select' | 'discard';
  /** 点击回调 */
  onClick?: () => void;
  /** 是否显示描述 */
  showDescription?: boolean;
  /** 是否显示提示 */
  showHint?: boolean;
}

/**
 * 卡牌项组件 - 扑克牌竖版布局
 */
export const CardItem: React.FC<CardItemProps> = React.memo(({
  card,
  isSelected = false,
  disabled = false,
  selectionMode = 'none',
  onClick,
  showDescription = false,
  showHint = false,
}) => {
  const typeConfig = CARD_TYPE_STYLE_CONFIG[card.type];
  const riskStyle = RISK_STYLES[card.riskLevel];
  const TypeIcon = CARD_TYPE_ICON[card.type] || Zap;

  // 选择状态样式
  const selectionClass = isSelected
    ? selectionMode === 'discard'
      ? 'ring-2 ring-red-500 ring-offset-2 scale-[1.02]'
      : 'ring-2 ring-[#9B8EC4] ring-offset-2 scale-[1.02]'
    : '';

  // 禁用状态
  const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`
        relative
        w-full
        aspect-[2/3.2]
        ${isSelected ? typeConfig.bgGradient : 'bg-white'}
        border-2 ${isSelected ? typeConfig.border : 'border-[#E5E0D8]'}
        rounded-lg
        shadow-md
        overflow-hidden
        ${selectionClass}
        ${disabledClass}
        ${onClick && !disabled ? 'cursor-pointer hover:shadow-lg hover:-translate-y-1' : ''}
        transition-all duration-200
        group
        flex flex-col
      `}
    >
      {/* 选中标记 */}
      {isSelected && selectionMode === 'select' && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#9B8EC4] flex items-center justify-center shadow-md z-20">
          <Check size={14} className="text-white" />
        </div>
      )}
      
      {/* 弃牌标记 */}
      {isSelected && selectionMode === 'discard' && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center shadow-md z-20">
          <Trash2 size={14} className="text-white" />
        </div>
      )}

      {/* === 顶部区域: 风险等级徽章 + 类型标签 === */}
      <div className="relative flex justify-between items-start p-2 z-10">
        {/* 左上角: 风险等级小徽章 */}
        <div className={`
          w-6 h-6
          ${riskStyle.badge}
          rounded-full 
          flex items-center justify-center 
          text-white text-[7px] font-bold
          shadow-sm
          border border-white/50
        `} title={riskStyle.label}>
          {riskStyle.icon}
        </div>

        {/* 右上角: 类型标签 */}
        <div className={`
          text-[9px] font-medium
          ${typeConfig.textColor}
          ${isSelected ? 'bg-white/70' : typeConfig.iconBg}
          px-1.5 py-0.5
          rounded
          border ${typeConfig.border}
        `}>
          {card.type}
        </div>
      </div>

      {/* === 中部区域: 类型图标 === */}
      <div className="flex-1 flex items-center justify-center relative">
        {/* 装饰圆环 */}
        <div className={`
          absolute w-14 h-14 rounded-full
          border-2 border-dashed ${typeConfig.border}
          opacity-20
        `} />
        
        {/* 内圈 */}
        <div className={`
          absolute w-10 h-10 rounded-full
          ${typeConfig.iconBg}
          opacity-50
        `} />

        {/* 主图标 */}
        <div className={`
          relative w-12 h-12
          ${typeConfig.iconBg}
          rounded-xl
          flex items-center justify-center
          border-2 ${typeConfig.border}
          shadow-md
        `}>
          <TypeIcon className={`w-6 h-6 ${typeConfig.iconColor}`} strokeWidth={2} />
        </div>

        {/* 四角小装饰 */}
        {isSelected && (
          <>
            <div className={`absolute top-0 left-2 ${typeConfig.accentColor}`}>
              <Star className="w-1.5 h-1.5" fill="currentColor" />
            </div>
            <div className={`absolute top-0 right-2 ${typeConfig.accentColor}`}>
              <Star className="w-1.5 h-1.5" fill="currentColor" />
            </div>
            <div className={`absolute bottom-0 left-2 ${typeConfig.accentColor}`}>
              <Star className="w-1.5 h-1.5" fill="currentColor" />
            </div>
            <div className={`absolute bottom-0 right-2 ${typeConfig.accentColor}`}>
              <Star className="w-1.5 h-1.5" fill="currentColor" />
            </div>
          </>
        )}
      </div>

      {/* === 底部区域 === */}
      <div className="relative p-2 z-10 flex flex-col items-center gap-1">
        {/* 卡牌名称 */}
        <div className={`
          text-xs font-bold ${typeConfig.textColor}
          text-center leading-tight
          line-clamp-2
          w-full
        `}>
          {card.name}
        </div>

        {/* 描述 */}
        {showDescription && (
          <p className="text-[8px] text-[#6B7280] text-center line-clamp-2 px-1">
            {card.description}
          </p>
        )}

        {/* 提示 */}
        {showHint && (
          <p className="text-[7px] text-[#9B8EC4] italic text-center line-clamp-1">
            💡 {card.hint}
          </p>
        )}

        {/* CP 范围 */}
        <div className={`
          text-[9px] font-medium
          ${typeConfig.textColor}
          opacity-70
          bg-white/50
          px-2 py-0.5
          rounded-full
        `}>
          {card.cpRange} CP
        </div>
      </div>

      {/* 装饰线 */}
      <div className={`absolute top-0 left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-current to-transparent ${typeConfig.accentColor} opacity-30`} />
      <div className={`absolute bottom-0 left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-current to-transparent ${typeConfig.accentColor} opacity-30`} />

      {/* 选中时的背景覆盖 */}
      {isSelected && (
        <div className={`absolute inset-0 ${typeConfig.bgGradient} opacity-30 pointer-events-none`} />
      )}
    </button>
  );
});

CardItem.displayName = 'CardItem';
